import React, { useId, useState } from 'react';
import clsx from 'clsx';
import {
  CollapseKey,
  CollapsePanel,
  CollapsePanelProps,
} from './_collapse-panel';

type CollapseProps = {
  /** One panel open at a time. */
  accordion?: boolean;
  /** Key(s) of the panel(s) open on first render (uncontrolled). */
  defaultActiveKey?: CollapseKey | CollapseKey[];
  className?: string;
  children?: React.ReactNode;
};

function normalizeKeys(key?: CollapseKey | CollapseKey[]): string[] {
  if (key === undefined) return [];

  return (Array.isArray(key) ? key : [key]).map(String);
}

function panelKeyOf(child: React.ReactElement, fallbackIndex: number): string {
  const explicit = (child.props as CollapsePanelProps).panelKey;
  if (explicit !== undefined) return String(explicit);
  if (child.key !== null) return String(child.key);

  return String(fallbackIndex);
}

export function Collapse({
  accordion = false,
  defaultActiveKey,
  className,
  children,
}: CollapseProps) {
  const baseId = useId();
  const [activeKeys, setActiveKeys] = useState<string[]>(() => {
    const seeded = [...normalizeKeys(defaultActiveKey)];
    React.Children.forEach(children, (child, index) => {
      if (
        React.isValidElement(child) &&
        (child.props as CollapsePanelProps).defaultOpen
      ) {
        seeded.push(panelKeyOf(child, index));
      }
    });

    return accordion ? seeded.slice(0, 1) : seeded;
  });

  const toggle = (key: string) =>
    setActiveKeys((prev) => {
      const isOpen = prev.includes(key);

      if (accordion) return isOpen ? [] : [key];

      return isOpen ? prev.filter((k) => k !== key) : [...prev, key];
    });

  return (
    <div className={clsx('wk-collapsible', className)}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;

        const key = panelKeyOf(child, index);
        const disabled = child.props.collapsible === 'disabled';

        return React.cloneElement(child, {
          _isOpen: activeKeys.includes(key),
          _onToggle: disabled ? undefined : () => toggle(key),
          _bodyId: `${baseId}-${key}`,
        });
      })}
    </div>
  );
}

Collapse.Panel = CollapsePanel;
